import { useCallback, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useReviewerRestrictions } from '@/hooks/useReviewerRestrictions';
import { isPostFailed, isPostPublished, isPostScheduled } from '@/utils/calendar';
import type { Post, OccurrenceStatus } from '@/services/posts';
import type { ReleaseDenyReason } from '@/types/release';

// ============================================================================
// Release rulebook mirror
// ============================================================================
//
// The SERVER is the source of truth (backend `release-policy.service.ts#can`, enforced by
// `release-write-guard.service.ts`). This hook is its cheap CLIENT MIRROR for lists (posts
// table, calendar, variations): it must never be stricter or looser than the rulebook for the
// inputs it can see. Composers (PostCreator / VariationCreator) read the exact server verdict via
// `usePostReleasePermissions` instead. Keep the rules below in sync with `can()`:
//
//   workflow OFF (carte blanche / standalone): edit, delete, schedule: anyone. send for review: never.
//   workflow ON:
//     edit     in review -> no | released -> no | live (scheduled/publishing/published/partial) -> no
//              creator -> yes | reviewer-only -> only their OWN untouched seed draft
//     delete   in review -> no | published output -> creator | creator -> yes | reviewer-only -> own seed draft
//     send     creator, not in review, not released, has content
//     release  reviewer, in review
//     schedule creator AND (released OR free-pass item)
//
// FAILED / MISSED are deliberately NOT live (a failed draft can be fixed and re-sent).
//
// LIMITS OF THE MIRROR: the list payloads carry no `hasReleaseHistory` / `updatedBy`, so the seed-draft and
// free-pass checks below are approximations (`workflowStatus === 'UNSCHEDULED'` also covers legacy
// REJECTED/SKIPPED records; a creator's earlier save is invisible). The mirror can therefore be LOOSER than the
// server for reviewer-only seed drafts and free-pass items. The server stays authoritative (it enforces every
// write and the composers read its exact verdict); a looser mirror only shows an action the server then refuses.

type ReleaseWorkflowState = 'DRAFT' | 'IN_REVIEW' | 'CHANGES_REQUESTED' | 'RELEASED';

type WorkflowStatusInput = 'UNSCHEDULED' | 'PENDING' | 'CHANGES_REQUESTED' | 'APPROVED' | undefined | null;

/** Facts of ONE item (a post or one variation) the mirror needs. Build with the helpers below. */
export interface ReleaseItemFacts {
  /** Latest release record of THIS item (`workflowStatus`); undefined = never sent. */
  workflowStatus?: WorkflowStatusInput;
  /** Latest non-cancelled occurrence of THIS item. */
  occurrence?: OccurrenceStatus;
  /** Real output on a channel: PUBLISHED or PARTIAL occurrence. */
  hasPublishedOutput: boolean;
  /** Variations have no owner column, so they can never be a seed draft. */
  isVariation: boolean;
  /** Post.userId of the base post. */
  ownerUserId?: number;
  /** Own creation time (a variation uses its own). */
  createdAt?: string;
  /** Caption or media present (own or inherited). Unknown = true. */
  hasContent?: boolean;
}

const WORKFLOW_STATE: Record<NonNullable<WorkflowStatusInput>, ReleaseWorkflowState> = {
  UNSCHEDULED: 'DRAFT',
  PENDING: 'IN_REVIEW',
  CHANGES_REQUESTED: 'CHANGES_REQUESTED',
  APPROVED: 'RELEASED',
};

const workflowStateOf = (status: WorkflowStatusInput): ReleaseWorkflowState =>
  status ? WORKFLOW_STATE[status] : 'DRAFT';

/**
 * Occurrences that are LIVE (committed to publishing or already out): mirrors the backend
 * `LIVE_OCCURRENCE_STATUSES`. FAILED and MISSED are not live.
 */
// ssot-guard-allow: frontend boundary mirror of backend LIVE_OCCURRENCE_STATUSES (release-policy.service.ts)
const LIVE_OCCURRENCE_STATUSES: ReadonlySet<OccurrenceStatus> = new Set([
  'SCHEDULED',
  'PUBLISHING',
  'PUBLISHED',
  'PARTIAL',
]);

/** Live = committed to publishing or already out (scheduled / publishing / published / partial). */
const isLive = (item: ReleaseItemFacts): boolean =>
  item.hasPublishedOutput || (item.occurrence !== undefined && LIVE_OCCURRENCE_STATUSES.has(item.occurrence));

/**
 * The post's effective OccurrenceStatus. Prefers the backend-computed `post.displayStatus`
 * (collapse of the LATEST non-cancelled occurrence); raw `post.status` desyncs for
 * scheduled-publish posts, and for calendar entries it even carries the MAPPING status.
 * Falls back to the legacy signal union only when the backend did not populate it.
 */
const resolveEffectiveOccurrenceStatus = (post: Post): OccurrenceStatus | undefined => {
  // null = backend resolved "no occurrence" (never-scheduled draft): unknown, so do NOT
  // fall into the legacy isPostScheduled union.
  if (post.displayStatus === null) return undefined;
  if (post.displayStatus !== undefined) return post.displayStatus;
  if (isPostFailed(post)) return 'FAILED';
  if (isPostPublished(post)) return 'PUBLISHED';
  if (isPostScheduled(post)) return 'SCHEDULED';
  return undefined;
};

/** In-flight (PUBLISHING) or cancelled: never editable, with or without a release workflow (unchanged behaviour). */
export const isInFlightOrCancelledPost = (post: Post): boolean =>
  post.displayStatus === 'PUBLISHING' ||
  post.displayStatus === 'CANCELLED' ||
  post.status === 'PUBLISHING' ||
  post.status === 'CANCELLED';

export const postToReleaseFacts = (post: Post): ReleaseItemFacts => {
  const occurrence = resolveEffectiveOccurrenceStatus(post);
  return {
    workflowStatus: post.workflowStatus,
    occurrence,
    hasPublishedOutput: occurrence === 'PUBLISHED' || occurrence === 'PARTIAL',
    isVariation: false,
    ownerUserId: post.userId,
    createdAt: post.createdAt,
    hasContent: (post.caption || '').trim().length > 0 || (post.postMedia?.length ?? 0) > 0,
  };
};

interface VariationFactsInput {
  workflowStatus?: WorkflowStatusInput;
  displayStatus?: OccurrenceStatus;
  instancesSummary?: { scheduled: number; published: number; publishing?: number; partial?: number } | null;
  createdAt?: string;
  caption?: string | null;
  postMedia?: ReadonlyArray<unknown>;
}

/** Variation counterpart of `postToReleaseFacts` (the variation's OWN release record + occurrences). */
export const variationToReleaseFacts = (variation: VariationFactsInput): ReleaseItemFacts => {
  const summary = variation.instancesSummary ?? undefined;
  const occurrence: OccurrenceStatus | undefined =
    variation.displayStatus ??
    (summary
      ? (summary.partial ?? 0) > 0
        ? 'PARTIAL'
        : summary.published > 0
          ? 'PUBLISHED'
          : (summary.publishing ?? 0) > 0
            ? 'PUBLISHING'
            : summary.scheduled > 0
              ? 'SCHEDULED'
              : undefined
      : undefined);
  return {
    workflowStatus: variation.workflowStatus,
    occurrence,
    hasPublishedOutput: occurrence === 'PUBLISHED' || occurrence === 'PARTIAL',
    isVariation: true,
    createdAt: variation.createdAt,
    // a variation inherits the post's content; the server decides the exact send-for-review check
    hasContent: undefined,
  };
};

// ============================================================================
// Types
// ============================================================================

export interface PostPermissions {
  /**
   * True when the release workflow is active (agency brand AND carte blanche OFF).
   */
  workflowEnabled: boolean;

  /**
   * True when the viewer is restricted by the workflow (reviewer-only person, or staff in God Mode).
   */
  isWorkflowRestricted: boolean;

  /** Creator capability: staff with access, or a direct non-reviewer member. */
  isCreator: boolean;

  /**
   * Reviewer capability: direct REVIEWER member, or agency staff with the reviewer flag
   * (staff reviewers stay reviewers in God Mode, whatever their home role says).
   */
  isReleaseReviewer: boolean;

  /** Can the viewer edit this post? (rulebook `edit`) */
  canEditPost: (post: Post) => boolean;

  /** Can the viewer delete this post? (rulebook `delete`) */
  canDeletePost: (post: Post) => boolean;

  /** Can the viewer send this post for review? (rulebook `sendForReview`) */
  canSendForReview: (post: Post) => boolean;

  /** Can the viewer release / request changes on this post? (reviewer AND in review) */
  canApprovePost: (post: Post) => boolean;

  /** Mark Ready: hidden entirely when the workflow is active. */
  canMarkReady: (post: Post) => boolean;

  /** Mark Draft: hidden entirely when the workflow is active. */
  canMarkDraft: (post: Post) => boolean;

  /** Can the viewer schedule / publish this post? (rulebook `schedule`, incl. the free pass) */
  canSchedulePost: (post: Post) => boolean;

  /** Always true. */
  canDuplicatePost: (post: Post) => boolean;

  /** Item-level variants: pass `postToReleaseFacts(post)` / `variationToReleaseFacts(variation)`. */
  canEditItem: (item: ReleaseItemFacts) => boolean;
  canDeleteItem: (item: ReleaseItemFacts) => boolean;
  canSendItemForReview: (item: ReleaseItemFacts) => boolean;
  canScheduleItem: (item: ReleaseItemFacts) => boolean;
  /** Client-mirror deny reason for `edit` (null = editable); the composers prefer the server's reason. */
  editDenyReasonItem: (item: ReleaseItemFacts) => ReleaseDenyReason | null;
}

// ============================================================================
// Hook
// ============================================================================

export const usePostPermissions = (): PostPermissions => {
  const { user } = useAuth();
  const { isReviewer: homeRoleIsReviewer, isGodMode, isAgencyTeam, carteBlanche } = useReviewerRestrictions();

  // Workflow is active when an agency brand has carte blanche turned off.
  const workflowEnabled: boolean = isAgencyTeam && !carteBlanche;

  // /me sends `agencyIsReviewer`; `agencyCanApprove` is the deprecated old name.
  const agencyIsReviewer = !!(user?.agencyIsReviewer ?? user?.agencyCanApprove);
  const isSuperAdmin = user?.isSuperAdmin === true;

  // Roles exactly as the backend resolves them (`actorFromAccess`): staff in God Mode are creators
  // (and reviewers when flagged); direct REVIEWER members are reviewer-only; everyone else creates.
  const isCreator: boolean = isSuperAdmin || isGodMode || !homeRoleIsReviewer;
  const isReleaseReviewer: boolean = isGodMode ? agencyIsReviewer : homeRoleIsReviewer;

  const isWorkflowRestricted: boolean = workflowEnabled && (isGodMode || homeRoleIsReviewer);

  // Cutoff of the brand's carte blanche switch (free-pass rule), from the effective team.
  const effectiveTeam = isGodMode ? user?.currentTeam : user?.team;
  const cutoffMs: number | null = useMemo(() => {
    const iso = effectiveTeam?.carteBlancheDisabledAt;
    if (!iso) return null;
    const ms = new Date(iso).getTime();
    return Number.isNaN(ms) ? null : ms;
  }, [effectiveTeam?.carteBlancheDisabledAt]);

  const userId = user?.id;

  /** A reviewer-only person may edit/delete only their OWN untouched seed draft (approximation, see LIMITS above: no updatedBy / release history in lists). */
  const isOwnSeedDraft = useCallback(
    (item: ReleaseItemFacts): boolean =>
      workflowStateOf(item.workflowStatus) === 'DRAFT' &&
      !item.isVariation &&
      userId !== undefined &&
      item.ownerUserId === userId,
    [userId]
  );

  /** D1 free pass: created before the cutoff and never sent for review (approximation, see LIMITS above). */
  const isFreePass = useCallback(
    (item: ReleaseItemFacts): boolean => {
      if (!workflowEnabled || cutoffMs === null || !item.createdAt) return false;
      if (workflowStateOf(item.workflowStatus) !== 'DRAFT') return false;
      const created = new Date(item.createdAt).getTime();
      return !Number.isNaN(created) && created < cutoffMs;
    },
    [workflowEnabled, cutoffMs]
  );

  /** Why the viewer may not edit this item (rulebook `edit` deny reason), or null when they may. */
  const editDenyReasonItem = useCallback(
    (item: ReleaseItemFacts): ReleaseDenyReason | null => {
      // Workflow off: no release lock, but an in-flight (PUBLISHING) or cancelled item stays uneditable, as before
      if (!workflowEnabled) {
        return item.occurrence === 'PUBLISHING' || item.occurrence === 'CANCELLED' ? 'frozen_committed' : null;
      }
      const state = workflowStateOf(item.workflowStatus);
      if (state === 'IN_REVIEW') return 'frozen_in_review';
      if (state === 'RELEASED') return 'frozen_released';
      if (isLive(item)) return 'frozen_committed';
      if (isCreator) return null;
      if (isOwnSeedDraft(item)) return null;
      return state === 'DRAFT' ? 'not_seed_owner' : 'not_creator';
    },
    [workflowEnabled, isCreator, isOwnSeedDraft]
  );

  const canEditItem = useCallback(
    (item: ReleaseItemFacts): boolean => editDenyReasonItem(item) === null,
    [editDenyReasonItem]
  );

  const canDeleteItem = useCallback(
    (item: ReleaseItemFacts): boolean => {
      if (!workflowEnabled) return true;
      if (workflowStateOf(item.workflowStatus) === 'IN_REVIEW') return false;
      // released and NOT published, draft, changes requested: creators; published output: creators too
      if (isCreator) return true;
      return !item.hasPublishedOutput && isOwnSeedDraft(item);
    },
    [workflowEnabled, isCreator, isOwnSeedDraft]
  );

  const canSendItemForReview = useCallback(
    (item: ReleaseItemFacts): boolean => {
      if (!workflowEnabled) return false;
      if (!isCreator) return false;
      const state = workflowStateOf(item.workflowStatus);
      if (state === 'IN_REVIEW' || state === 'RELEASED') return false;
      return item.hasContent !== false;
    },
    [workflowEnabled, isCreator]
  );

  const canScheduleItem = useCallback(
    (item: ReleaseItemFacts): boolean => {
      if (!workflowEnabled) return true;
      if (!isCreator) return false;
      return workflowStateOf(item.workflowStatus) === 'RELEASED' || isFreePass(item);
    },
    [workflowEnabled, isCreator, isFreePass]
  );

  const canEditPost = useCallback(
    (post: Post): boolean => !isInFlightOrCancelledPost(post) && canEditItem(postToReleaseFacts(post)),
    [canEditItem]
  );
  const canDeletePost = useCallback((post: Post): boolean => canDeleteItem(postToReleaseFacts(post)), [canDeleteItem]);
  const canSendForReview = useCallback(
    (post: Post): boolean => canSendItemForReview(postToReleaseFacts(post)),
    [canSendItemForReview]
  );
  const canSchedulePost = useCallback(
    (post: Post): boolean => canScheduleItem(postToReleaseFacts(post)),
    [canScheduleItem]
  );

  const canApprovePost = useCallback(
    (post: Post): boolean => {
      if (!workflowEnabled) return false; // workflow_inactive
      return isReleaseReviewer && workflowStateOf(post.workflowStatus) === 'IN_REVIEW';
    },
    [workflowEnabled, isReleaseReviewer]
  );

  const canMarkReady = useCallback((_post: Post): boolean => !workflowEnabled, [workflowEnabled]);
  const canMarkDraft = useCallback((_post: Post): boolean => !workflowEnabled, [workflowEnabled]);
  const canDuplicatePost = useCallback((_post: Post): boolean => true, []);

  return {
    workflowEnabled,
    isWorkflowRestricted,
    isCreator,
    isReleaseReviewer,
    canEditPost,
    canDeletePost,
    canSendForReview,
    canApprovePost,
    canMarkReady,
    canMarkDraft,
    canSchedulePost,
    canDuplicatePost,
    canEditItem,
    canDeleteItem,
    canSendItemForReview,
    canScheduleItem,
    editDenyReasonItem,
  };
};
